import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGUAGES, useLanguage } from "../../lib/i18n";

export const LanguageSelector = ({ variant = "dark" }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/25 transition-colors duration-300"
        data-testid="language-selector-btn"
        aria-label="Select language"
      >
        <Globe size={15} />
        <span className="text-sm font-medium">{current.short}</span>
        <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 mt-2 w-40 rounded-2xl border border-white/10 bg-[#0A101F]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50"
            data-testid="language-menu"
          >
            {LANGUAGES.map((l) => (
              <li key={l.code}>
                <button
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors duration-200 ${
                    l.code === lang ? "text-white bg-white/[0.04]" : "text-slate-400 hover:text-white hover:bg-white/[0.03]"
                  }`}
                  data-testid={`language-option-${l.code}`}
                >
                  {l.label}
                  {l.code === lang && <Check size={14} className="text-blue-400" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
